import { getDb, type Db } from "@anima/db";
import { entries, series, type Entry } from "@anima/db/schema";
import { and, asc, desc, eq, inArray, lt, sql } from "drizzle-orm";

function errorMessages(error: unknown): string[] {
  if (!error || typeof error !== "object") {
    return [String(error)];
  }
  const messages: string[] = [];
  if ("message" in error && typeof error.message === "string") {
    messages.push(error.message);
  }
  if ("cause" in error) {
    messages.push(...errorMessages(error.cause));
  }
  return messages;
}

function fallbackForMissingDb<T>(error: unknown, fallback: T): T {
  const messages = errorMessages(error).join("\n");
  if (
    messages.includes("prepare") ||
    messages.includes("D1") ||
    messages.includes("undefined") ||
    messages.includes("not found") ||
    messages.includes("no such table") ||
    messages.includes("SQLITE_ERROR")
  ) {
    return fallback;
  }
  throw error;
}

export type EntryKind = Entry["kind"];

export type SeriesWithEntryCount = Awaited<ReturnType<typeof getSeriesList>>[number];

export async function getAllEntryPathsForKinds(kinds: EntryKind[], db?: Db) {
  if (kinds.length === 0) {
    return [];
  }
  try {
    db ??= getDb();
    const perPage = 100;
    let cursor: string | null = null;
    const output: { slug: string; kind: EntryKind; position: string }[] = [];
    while (true) {
      const query = db.select({ slug: series.slug, id: series.id }).from(series).limit(perPage);
      if (cursor) {
        query.where(lt(series.id, cursor));
      }
      const rows = await query.orderBy(desc(series.id));
      if (rows.length === 0) {
        break;
      }
      const seriesSlugs = new Map(rows.map((r) => [r.id, r.slug]));
      const entriesList = await db
        .select({ position: entries.position, seriesId: entries.seriesId, kind: entries.kind })
        .from(entries)
        .where(
          and(
            inArray(
              entries.seriesId,
              rows.map((r) => r.id),
            ),
            inArray(entries.kind, kinds),
            eq(entries.status, "published"),
          ),
        );
      for (const entry of entriesList) {
        const slug = seriesSlugs.get(entry.seriesId);
        if (slug) {
          output.push({ slug, kind: entry.kind, position: `${entry.position}` });
        }
      }
      cursor = rows[rows.length - 1]?.id ?? null;
    }
    return output;
  } catch (error) {
    console.error("Error fetching all entry paths:", error);
    return fallbackForMissingDb(error, []);
  }
}

export async function getAllSeriesKindSeasonPaths(db?: Db) {
  try {
    db ??= getDb();
    const perPage = 100;
    let cursor: string | null = null;
    const output: { slug: string; kind: EntryKind; season: string }[] = [];
    while (true) {
      const query = db.select({ slug: series.slug, id: series.id }).from(series).limit(perPage);
      if (cursor) {
        query.where(lt(series.id, cursor));
      }
      const rows = await query.orderBy(desc(series.id));
      if (rows.length === 0) {
        break;
      }
      const seriesSlugs = new Map(rows.map((r) => [r.id, r.slug]));
      const seasonRows = await db
        .selectDistinct({
          kind: entries.kind,
          seasonNumber: entries.seasonNumber,
          seriesId: entries.seriesId,
        })
        .from(entries)
        .where(
          and(
            inArray(
              entries.seriesId,
              rows.map((r) => r.id),
            ),
            eq(entries.status, "published"),
          ),
        );
      for (const row of seasonRows) {
        const slug = seriesSlugs.get(row.seriesId);
        if (slug) {
          output.push({ slug, kind: row.kind, season: `${row.seasonNumber}` });
        }
      }
      cursor = rows[rows.length - 1]?.id ?? null;
    }
    return output;
  } catch (error) {
    console.error("Error fetching all series kind season paths:", error);
    return fallbackForMissingDb(error, []);
  }
}

export async function getAllSeriesSlugs(db?: Db) {
  try {
    db ??= getDb();
    const perPage = 100;
    let cursor: string | null = null;
    const slugs: string[] = [];
    while (true) {
      const query = db.select({ slug: series.slug, id: series.id }).from(series).limit(perPage);
      if (cursor) {
        query.where(lt(series.id, cursor));
      }
      const rows = await query.orderBy(desc(series.id));
      if (rows.length === 0) {
        break;
      }
      for (const row of rows) {
        slugs.push(row.slug);
      }
      cursor = rows[rows.length - 1]?.id ?? null;
    }
    return slugs;
  } catch (error) {
    console.error("Error fetching all series slugs:", error);
    return fallbackForMissingDb(error, []);
  }
}

export async function getSeriesList(limit = 120, db?: Db) {
  try {
    db ??= getDb();

    const seriesRows = await db.select().from(series).orderBy(desc(series.updatedAt)).limit(limit);

    if (seriesRows.length === 0) {
      return [];
    }

    const ids = seriesRows.map((row) => row.id);

    const [counts, kindsRows] = await Promise.all([
      db
        .select({
          seriesId: entries.seriesId,
          count: sql<number>`count(*)`,
        })
        .from(entries)
        .where(and(eq(entries.status, "published"), inArray(entries.seriesId, ids)))
        .groupBy(entries.seriesId),
      db
        .selectDistinct({
          seriesId: entries.seriesId,
          kind: entries.kind,
        })
        .from(entries)
        .where(and(eq(entries.status, "published"), inArray(entries.seriesId, ids)))
        .orderBy(asc(entries.kind)),
    ]);

    const countBySeries = new Map(counts.map((row) => [row.seriesId, row.count]));
    const kindsBySeries = new Map<string, EntryKind[]>();
    for (const row of kindsRows) {
      const list = kindsBySeries.get(row.seriesId);
      if (list) {
        list.push(row.kind);
      } else {
        kindsBySeries.set(row.seriesId, [row.kind]);
      }
    }

    return seriesRows.map((row) => ({
      ...row,
      publishedEntryCount: countBySeries.get(row.id) ?? 0,
      kinds: kindsBySeries.get(row.id) ?? [],
    }));
  } catch (error) {
    return fallbackForMissingDb(error, []);
  }
}

export async function getSeriesByKind(kind: EntryKind, limit = 120, db?: Db) {
  try {
    db ??= getDb();

    const kindIds = await db
      .select({ id: entries.seriesId })
      .from(entries)
      .where(and(eq(entries.kind, kind), eq(entries.status, "published")))
      .groupBy(entries.seriesId)
      .limit(limit);

    const ids = kindIds.map((r) => r.id);

    if (ids.length === 0) {
      return [];
    }

    const seriesRows = await db
      .select()
      .from(series)
      .where(inArray(series.id, ids))
      .orderBy(desc(series.updatedAt))
      .limit(limit);

    if (seriesRows.length === 0) {
      return [];
    }

    const seriesIds = seriesRows.map((row) => row.id);

    const counts = await db
      .select({
        seriesId: entries.seriesId,
        count: sql<number>`count(*)`,
      })
      .from(entries)
      .where(
        and(
          eq(entries.status, "published"),
          eq(entries.kind, kind),
          inArray(entries.seriesId, seriesIds),
        ),
      )
      .groupBy(entries.seriesId);

    const countBySeries = new Map(counts.map((row) => [row.seriesId, row.count]));

    return seriesRows.map((row) => ({
      ...row,
      publishedEntryCount: countBySeries.get(row.id) ?? 0,
      kinds: [kind] satisfies EntryKind[],
    }));
  } catch (error) {
    console.error("Error fetching series by kind:", kind, error);
    return fallbackForMissingDb(error, []);
  }
}

export function getMediaUrl(r2Key: string) {
  return `/api/artifacts?key=${encodeURIComponent(r2Key)}`;
}

export function getOptimizedMediaUrl(r2Key: string, width: number, quality?: number) {
  const base = `/api/artifacts?key=${encodeURIComponent(r2Key)}`;
  if (process.env.NODE_ENV === "development") return base;
  const params = [`width=${width}`, "format=auto"];
  if (quality) params.push(`quality=${quality}`);
  return `/cdn-cgi/image/${params.join(",")}/${base.replace(/^\//, "")}`;
}
